import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, IconButton, Modal, Switch, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import modalStyle from "../../styles/modalStyle";
import Permissions from "../../interfaces/permissions";
import DropDown from "../baseComponents/dropdown";

interface HandlePermissionsModalProps {
    open: boolean;
    onClose: () => void;
    selectedPermission?: Permissions;
    getAPIFunction: () => Promise<void>;
    modalMode: string;
};

const permissionTypes = { normal: 'normal', licensed: 'licensed' };
const horizontalStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const HandlePermissionsModal: React.FC<HandlePermissionsModalProps> = ({ open, onClose, getAPIFunction, selectedPermission, modalMode }) => {
    const [permissionName, setPermissionName] = useState<string>('');
    const [permissionType, setPermissionType] = useState<string>(permissionTypes.normal);
    const [isPermissionActive, setIsPermissionActive] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (selectedPermission && modalMode === "edit") {
            setPermissionName(selectedPermission.permission_name);
            setIsPermissionActive(selectedPermission.is_active);
            setPermissionType(selectedPermission.permission_type ? permissionTypes.licensed : permissionTypes.normal);
        }
    }, [selectedPermission, modalMode]);

    const createOrEditPermission = async () => {
        try {
            if (!permissionName) {
                alert('Please enter valid permission name');
                return;
            }
            setLoading(true);
            let response;
            const body = [{
                permission_name: permissionName,
                permission_type: permissionType === permissionTypes.normal ? null : permissionType,
                is_active: selectedPermission ? isPermissionActive : true
            }];
            if (selectedPermission && modalMode === "edit") {
                response = await apiClient.put('/api/v1/permissions', body);
            } else {
                response = await apiClient.post('/api/v1/permissions', body);
            }
            if (response.status === 200) {
                if (selectedPermission && modalMode === "edit") {
                    alert('Permission updated successfully');
                } else {
                    alert('New permission created successfully');
                }
                handleClose();
                await getAPIFunction();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPermissionName('');
        setPermissionType(permissionTypes.normal);
        setIsPermissionActive(true);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <>
                        <Box sx={horizontalStyle}>
                            <Typography id="modal-title" variant="h6">
                                {selectedPermission && modalMode === "edit" ? 'Edit' : 'Add New'} Permission
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={horizontalStyle}>
                            <TextField
                                label="Enter Permission Name"
                                value={permissionName}
                                onChange={e => setPermissionName(e.target.value)}
                                style={{ width: '49%' }}
                            />
                            <DropDown
                                label="Select Permission Type"
                                items={Object.values(permissionTypes)}
                                selectedValue={permissionType}
                                onSelection={setPermissionType}
                                style={{ width: '49%' }}
                            />
                        </Box>
                        {selectedPermission && modalMode === "edit" && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPermissionActive}
                                        onChange={e => setIsPermissionActive(e.target.checked)}
                                        name="Is Permission Active"
                                        size="medium"
                                    />
                                }
                                sx={{ mt: 2 }}
                                label="Will this permission be active?*"
                                labelPlacement="end"
                            />
                        )}
                        <Box mt={2} textAlign="right">
                            <Button variant="contained" color="primary" onClick={createOrEditPermission}>
                                <Typography>Submit</Typography>
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default HandlePermissionsModal;